// src/stores/module.js
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { create } from 'zustand';
import useZustand from 'hooks/useZustand';
import { devtools } from 'zustand/middleware';
import app from 'configs/ConfigIndex';
import apiConfig from 'configs/fetchInterceptor';

import { getStorageAuthItem, initialState as storedUser, useAuthStore } from './auth';
import { getQuery, mergeUrlParamsQuery, getParams } from 'utils/urlUtils';
import { logErrorSilent } from 'utils/errorLogger';
import consoleX from 'utils/consoleX';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************

const LOCAL_STORAGE_KEY = 'storage-module';
const FILE_TITLE = 'MODULE STORE';
let tmr;

// *******************************************************************************
// STATE
// *******************************************************************************
export const initialState = {
  config: null,
  preferences: {},
  currentRouteKey: null,
  type: null, // 'list', 'document', 'dashboard', 'grid' or 'kanban'
  pages: {}, // Init: {route: null, query: {}, data:[], summary:{}}
  currentPage: null,
  // loading: false,
  // loadingModule: false, // explicit module-level loading flag
  breadcrumbs: [],

  // Navigation history stack for back button
  navigationHistory: [],

  // Page transition object to track page navigation and prevent old tabs from executing
  // Format: {from: 'pageName', to: 'pageName'} or null when no transition
  transition: null,

  // Active view selector (init, dashboard, reports, etc.)
  // Reset to 'init' on setPage/setModule
  currentView: 'init',

  // Document view mode synced from DocumentProvider
  // Used to determine if we're in 'add' mode without checking URL
  viewMode: null,

  // Document duplication flag
  // When set (timestamp), indicates document is being duplicated
  // Used by SimpleFormProvider to send ALL fields on save (not just changed ones)
  documentDuplicated: null,

  // configuration
  listSettings: {},
  pageSettings: {},
  parameters: {},

  // Debug only - captured on demand
  formDebug: {},

  fn: {
    // safe refresh hook
    refresh: ({ query } = {}) => {
      if (typeof window.__API_GET__ === 'function') {
        window.__API_GET__({ query });
      }
    },
  },
};

// *******************************************************************************
// METHODS
// *******************************************************************************
function buildStorageKey(appId) {
  return `${appId || app.ID}:${LOCAL_STORAGE_KEY}`;
}

export function getStorageModuleItem(key) {
  const storedModule = useZustand('module', 'config', false);
  const storedSubscription = useZustand('auth', 'subscription', false);
  const storageUser = useZustand('auth', 'storageUser', false);
  const field = buildStorageKey(storedModule?.appId);

  let subscriptionId = storedSubscription?._id;
  if (!subscriptionId) {
    const userId = storageUser?._id;
    const k = `userId[${userId}].subscriptionId`;
    subscriptionId = getStorageAuthItem(k);
  }
  if (!storedModule?.id) return;

  let lsData;
  const moduleName = storedModule?.id;
  try {
    lsData = JSON.parse(localStorage.getItem(field));
  } catch (e) {
    logErrorSilent(e, {
      context: 'moduleStore.getStorageModuleItem (JSON.parse)',
      extraData: { field, key, moduleName },
    });
  }

  return lsData?.[`subscriptionId[${storedSubscription?._id || 'n/a'}]`]?.[moduleName]?.[key];
}

export function setStorageModuleItem(key, value) {
  const storedModule = useZustand('module', 'config', false);
  const storedSubscription = useZustand('auth', 'subscription', false);
  const field = buildStorageKey(storedModule?.appId);
  if (!storedModule?.id) return;

  let lsData;
  try {
    lsData = JSON.parse(localStorage.getItem(field));
  } catch (e) {
    logErrorSilent(e, {
      context: 'moduleStore.setStorageModuleItem (JSON.parse)',
      extraData: { field, key },
    });
  }
  if (!lsData) lsData = {};

  const subKey = `subscriptionId[${storedSubscription?._id || 'n/a'}]`;
  if (!lsData[subKey]) lsData[subKey] = {};
  if (!lsData[subKey][storedModule.id]) {
    lsData[subKey][storedModule.id] = {};
  }

  const obj = lsData[subKey][storedModule.id];
  if (value !== undefined && value !== null) {
    obj[key] = value;
  } else {
    delete obj[key];
  }
  localStorage.setItem(field, JSON.stringify(lsData));
}

export function removeStorageModuleItem(key) {
  const storedModule = useZustand('module', 'config', false);
  const storedSubscription = useZustand('auth', 'subscription', false);
  const field = buildStorageKey(storedModule?.appId);
  if (!storedModule?.id) return;

  let lsData;
  try {
    lsData = JSON.parse(localStorage.getItem(field));
  } catch (e) {
    logErrorSilent(e, {
      context: 'moduleStore.removeStorageModuleItem (JSON.parse)',
      extraData: { field, key },
    });
  }
  if (!lsData) return;

  const subKey = `subscriptionId[${storedSubscription?._id || 'n/a'}]`;
  if (lsData[subKey]?.[storedModule.id]?.[key]) {
    delete lsData[subKey][storedModule.id][key];
    localStorage.setItem(field, JSON.stringify(lsData));
  }
}

// *******************************************************************************
// STORE
// *******************************************************************************
const store = create(
  devtools(
    (set, get) => {
      return {
        ...initialState,

        setModule: async ({ moduleConfig, pageName = 'init', noComponent }) => {
          const { setPage, config, fn, setBreadcrumbs, setPreferences } = get();
          const storedAppModules = useZustand('app', 'appModules', false);
          const storedUser = useZustand('auth', 'user', false);
          const { ready, setReady } = get();

          if (ready) setReady(false);

          // Check if module is already loaded
          const isModuleAlreadyLoaded = config?.id && config?.id === moduleConfig?.id;

          // Execute parametersSeed for first-time module setup only
          // The seed should only run when the module has NO saved parameters
          if (moduleConfig?.id && typeof moduleConfig.parametersSeed === 'function') {
            let retryCount = 0;
            const maxRetries = 10; // Max 10 retries (1 second total wait)

            const executeParametersSeed = () => {
              const storedParameters = useZustand('app', 'parameters', false);
              const moduleId = moduleConfig.id;

              // Wait for SSE to connect and deliver the initial parameters response.
              // parameters === null means SSE hasn't responded yet (initial store state).
              // Any non-null value (including {} or { modules: null }) means SSE connected
              // proceed even if modules is empty (new subscription with no stored params).
              if (storedParameters === null) {
                if (retryCount < maxRetries) {
                  retryCount++;
                  setTimeout(executeParametersSeed, 100);
                }
                return;
              }

              // Execute seed - each seed function checks internally if data already exists
              // and skips categories that are already seeded
              moduleConfig.parametersSeed({
                moduleId,
                storedParameters,
              });
            };

            // Start checking for parameters after SSE has had time to connect
            setTimeout(executeParametersSeed, 500);
          }

          if (isModuleAlreadyLoaded) return;

          if (!moduleConfig) {
            set({
              config: null,
              pages: {
                init: {
                  header: {
                    visible: true,
                    style: 'default',
                    avatar: {
                      visible: true,
                      placeholder: 'photo',
                    },
                    navigation: {
                      showTitle: true,
                      showBreadcrumb: null,
                    },
                    buttons: {
                      filter: false,
                      refresh: false,
                      goBack: false,
                      parameters: false,
                    },
                    stats: {
                      showTotalRecords: false,
                    },
                  },
                  settings: {},
                  tabs: {},
                },
              },
              currentPage: null,
            });

            apiConfig.defaults.headers['x-module'] = null;
            setPage({ pageName, noComponent });

            if (!get().ready) {
              tmr = setTimeout(() => {
                setReady(true);
                clearTimeout(tmr);
              }, 200);
            }
            return;
          }

          apiConfig.defaults.headers['x-module'] = moduleConfig?.id || null;

          // Ensure refreshParameters is available before calling
          if (moduleConfig?.id) {
            const { refreshParameters } = fn || {};

            if (typeof refreshParameters === 'function') {
              refreshParameters().then();
            } else {
              // Function not ready yet, wait for initFunctions to complete
              const checkAndCall = () => {
                const currentFn = get().fn || {};
                if (typeof currentFn.refreshParameters === 'function') {
                  currentFn.refreshParameters().then();
                } else {
                  // Retry after a small delay
                  setTimeout(checkAndCall, 50);
                }
              };
              checkAndCall();
            }
          }

          const modulePages = storedAppModules?.[moduleConfig?.id]?.pages;
          setBreadcrumbs({ add: moduleConfig?.url?.split('/'), reset: true });

          const sp = storedUser?.systemPreferences?.modules?.find(
            m => moduleConfig?.id && m && m?.id === moduleConfig.id
          ) || { id: moduleConfig?.id || null };

          // Reset listSettings when changing modules to prevent stale folder state
          set({ config: moduleConfig, pages: modulePages || null, listSettings: {} });
          setPreferences(sp);

          setPage({ pageName, noComponent });
        },

        setPageBack: ({ navigate }) => {
          const { navigationHistory, currentPage, setPage } = get();

          // If current page defines a fixed back destination, always use it
          const fixedBackPage = currentPage?.settings?.backPage;
          if (fixedBackPage) {
            removeStorageModuleItem('backTo');
            setPage({ navigate, pageName: fixedBackPage, skipHistorySave: true });
            return;
          }

          // Try to get previous page from navigation history stack
          let previousPage = null;
          let newHistory = [...navigationHistory];

          if (newHistory.length > 0) {
            // Get last entry from stack
            previousPage = newHistory.pop();
            set({ navigationHistory: newHistory });
          } else {
            // History is empty (probably after F5), try to recover from localStorage
            const savedBackTo = getStorageModuleItem('backTo');

            if (savedBackTo?.page) {
              // CRITICAL: Validate saved params - skip if any param contains ":" (URL template)
              const hasInvalidParams =
                savedBackTo.params &&
                Object.values(savedBackTo.params).some(
                  value => typeof value === 'string' && value.includes(':')
                );

              if (!hasInvalidParams) {
                previousPage = {
                  pageName: savedBackTo.page,
                  params: savedBackTo.params || {},
                  query: savedBackTo.query || {},
                };
              } else {
                console.warn(
                  '[module.setPageBack] Skipping backTo - params contain URL templates',
                  {
                    savedBackTo,
                  }
                );
                // Clean up corrupted localStorage
                removeStorageModuleItem('backTo');
              }
            }
          }

          // Fallback: use browser history when no internal navigation history exists
          // This correctly returns to the actual previous page (e.g., dashboard)
          // regardless of which module or route the user came from
          if (!previousPage) {
            removeStorageModuleItem('backTo');
            navigate(-1);
            return;
          }

          // Navigate to previous page
          setPage({
            navigate,
            pageName: previousPage.pageName,
            params: previousPage.params || {},
            query: previousPage.query || {},
            skipHistorySave: true,
          });

          // Clean up localStorage
          removeStorageModuleItem('backTo');
        },

        setPage: ({
          navigate,
          pageName = 'init',
          params,
          query,
          state = {},
          saveHistory = false,
          onlyUpdateUrl = false,
          skipHistorySave = false, // NEW: flag to skip saving to history (used by setPageBack)
          ignoresLoadingPage = false,
          noComponent,
        }) => {
          // Remove focus from active element to prevent aria-hidden warning during navigation
          if (document.activeElement instanceof HTMLElement) document.activeElement.blur();

          const { ready, setReady, currentPage } = get();

          // CRITICAL: Set transition object ONLY during real navigation
          // Don't set on initial page load (no currentPage) or URL-only updates
          // This prevents blocking useApi execution after F5
          if (!onlyUpdateUrl && currentPage?.name && typeof navigate === 'function') {
            set({ transition: { from: currentPage.name, to: pageName } });
          }

          if (ready) setReady(false);

          const setLoadingPageStore = useZustand('boot', 'setLoadingPage', false);
          const storedLoadingPage = useZustand('boot', 'loadingPage', false);
          const { config, pages, setPageSettings, setCurrentRouteKey, navigationHistory } = get();

          // Save the current page to navigation history before navigating (if not coming from back button)
          if (
            !skipHistorySave &&
            !onlyUpdateUrl &&
            currentPage?.name &&
            typeof navigate === 'function'
          ) {
            // Extract params and query from current URL
            const currentUrl = window.location.pathname + window.location.search;
            const currentPathname = window.location.pathname;

            // Build schema URL from config.url + currentPage.routing.endpoint
            const schemaUrl = config?.url
              ? config.url + (currentPage?.routing?.endpoint || '')
              : null;

            // Extract params using getParams with schema
            const currentParams = schemaUrl ? getParams(currentPathname, schemaUrl) : {};

            // CRITICAL: Validate extracted params - if any param contains ":" it's a template not replaced
            // This happens during abort/navigation transitions - skip saving history in this case
            const hasInvalidParams = Object.values(currentParams).some(
              value => typeof value === 'string' && value.includes(':')
            );

            if (!hasInvalidParams) {
              // Extract query from URL search params
              const currentQuery = {};
              const urlSearchParams = new URLSearchParams(window.location.search);
              urlSearchParams.forEach((value, key) => {
                currentQuery[key] = value;
              });

              // Build history entry
              const historyEntry = {
                pageName: currentPage.name,
                params: currentParams,
                query: currentQuery,
                url: currentUrl,
                timestamp: Date.now(),
              };

              // Add to navigation history stack (max 10 entries)
              const newHistory = [...navigationHistory, historyEntry].slice(-10);
              set({ navigationHistory: newHistory });

              // Save params and query to localStorage for persistence after F5
              // Store as a single backTo object to maintain architecture pattern
              setStorageModuleItem('backTo', {
                page: currentPage.name,
                tab: currentQuery.tab || null,
                params: currentParams,
                query: currentQuery,
              });
            } else {
              console.warn(
                '[module.setPage] Skipping history save - params contain URL templates',
                {
                  currentParams,
                  currentPathname,
                  schemaUrl,
                }
              );
            }
          }

          const mainPage = get().config?.initPage || 'init';
          if (
            !onlyUpdateUrl &&
            !storedLoadingPage &&
            !ignoresLoadingPage &&
            (pageName !== mainPage || currentPage?.name !== mainPage)
          ) {
            setLoadingPageStore({ visible: true, name: pageName });
          }

          // CRITICAL: Execute $done synchronously to ensure currentPage is set before
          // the component renders. The previous setTimeout caused race conditions on F5
          // where useUrl would run before currentPage was populated, resulting in
          // params not being parsed and API calls being skipped.
          $done();

          function $done() {
            const pageConfig = pages?.[pageName] || {};

            // navigate to the page
            if (typeof navigate === 'function') {
              const url = mergeUrlParamsQuery({
                url: config?.url,
                endpoint: pageConfig?.routing?.endpoint,
                params,
                query,
              });

              // CRITICAL: Don't navigate to URLs with unresolved templates
              if (url && url.includes(':')) {
                console.warn(
                  '[module.setPage] URL contains unresolved templates, skipping navigation',
                  {
                    url,
                    params,
                    endpoint: pageConfig?.routing?.endpoint,
                  }
                );
                // Navigate to dashboard/overview as fallback
                navigate('/app/dashboard/overview', { replace: true });
                return;
              }

              navigate(url, { replace: !saveHistory }, state);
            }

            savePage();
            // setTimeout(() => savePage(), 200);

            function savePage() {
              // save pageSettings

              const ps = pageConfig?.settings;
              if (query?.tab) ps.initialTab = query.tab;
              setPageSettings(ps);

              // save the current route key
              if (config?.url) setCurrentRouteKey(config.url);

              // save the current page

              set({
                currentView: 'init',
                currentPage: {
                  ...pageConfig,
                  data: null,
                  summary: null,
                  _dataFromUrl: null,
                },
                pages: {
                  ...pages,
                  [pageName]: {
                    ...pages[pageName],
                    data: null,
                    summary: null,
                    _dataFromUrl: null,
                  },
                },
              });

              if (noComponent) setReady(true);
            }
          }
        },

        setUpdatePage: (value, pageName) => {
          const { pages, currentPage } = get();
          if (typeof value !== 'object') return;

          if (!pageName) {
            if (!currentPage?.name) return;
            pageName = currentPage?.name;
          }

          if (currentPage?.name === pageName) {
            set({ currentPage: { ...currentPage, ...value } });
          }

          if (pages?.[pageName]) {
            set({
              pages: {
                ...pages,
                [pageName]: { ...pages[pageName], ...value },
              },
            });
          }
        },

        setPageData: ({ data, summary = null, page = null, force = false }) => {
          const { pages, currentPage, viewMode } = get();

          // CRITICAL: Check if we're in "add" mode (new document)
          // If viewMode is 'add' and this is NOT a forced update, ignore stale data from old requests
          const isAddMode = viewMode === 'add';

          if (isAddMode && data && !force) {
            return;
          }

          const pageName = page || currentPage?.name || 'init';
          const pageDestination = pages?.[pageName];

          // Check persistInStore from currentPage.api, pageDestination.api, or force flag
          const shouldPersist =
            force || currentPage?.api?.persistInStore || pageDestination?.api?.persistInStore;

          if (!shouldPersist) {
            return;
          }
          if (!pageDestination) {
            return;
          }

          const pageWithData = { data, summary };

          const updatedPages = { ...pages, [pageName]: { ...pageDestination, ...pageWithData } };
          const updates = { pages: updatedPages };

          if (currentPage?.name === pageName) {
            updates.currentPage = { ...currentPage, ...pageWithData };
          }

          set(updates);
        },

        setType: value => set({ type: value }),

        setPreferences: value => set({ preferences: value }),

        setTransition: value => set({ transition: value }),

        // Set view mode synced from DocumentProvider
        setCurrentView: value => set({ currentView: value || 'init' }),

        setViewMode: value => set({ viewMode: value }),

        // Set document duplicated flag
        // done=true clears the flag, done=false sets it to timestamp
        setDocumentDuplicatedStore: done => {
          const value = done ? null : +new Date();
          set({ documentDuplicated: value });
        },

        // Reset currentPage completely - used by abortDocumentAccess to ensure clean state
        // This is different from setUpdatePage which only updates specific fields
        resetCurrentPage: () => set({ currentPage: null }),

        setListSettings: value => {
          const { config, listSettings } = get();
          if (value && typeof value === 'object') {
            const ls =
              !listSettings?.moduleId || listSettings?.moduleId === config?.id ? listSettings : {};
            set({ listSettings: { ...ls, ...value } });
          } else {
            set({ listSettings: {} });
          }
        },

        setPageSettings: obj => set({ pageSettings: { ...obj } }),

        setPageSettings_OLD_BKP: value => {
          const { config, pageSettings } = get();

          if (value && typeof value === 'object') {
            set({
              pageSettings: {
                ...(!pageSettings?.moduleId || pageSettings?.moduleId === config?.id
                  ? pageSettings
                  : {}),
                ...value,
              },
            });
          } else {
            set({ pageSettings: {} });
          }
        },

        // when page becomes ready, hide all loadings with delay to prevent seeing component mounting
        setReady: value => {
          const { ready, currentPage, config } = get();

          if (value && !get().ready) {
            consoleX({
              message: `Page Ready: ${value}`,
              flag: 'debug',
              emoji: '>>',
              title: FILE_TITLE,
              info: {
                setReady: value,
                Data: {
                  module: config?.id,
                  page: currentPage?.name,
                  tab: getQuery()?.tab,
                },
              },
            });
          }

          const setLoadingAppStore = useZustand('boot', 'setLoadingApp', false);
          const setLoadingModuleStore = useZustand('boot', 'setLoadingModule', false);
          const setLoadingPageStore = useZustand('boot', 'setLoadingPage', false);

          if (value) {
            setLoadingPageStore(false);
            setLoadingModuleStore(false);

            // During first boot, defer hiding loadingApp until auth.user is populated.
            // Without this guard, pages with no API (url: null) call done() immediately,
            // hiding the loading screen before signInByToken has resolved,
            // causing a flash of unauthenticated UI (no avatar, no menu, no user name).
            const firstBootActive = useZustand('boot', 'firstBootActive', false);
            const currentUser = useAuthStore.getState().user;

            if (firstBootActive && !currentUser) {
              const unsub = useAuthStore.subscribe((state, prevState) => {
                if (state.user && !prevState.user) {
                  setLoadingAppStore(false);
                  unsub();
                }
              });
            } else {
              setLoadingAppStore(false);
            }
          }

          if (ready === value) return;

          set({ ready: value });
        },

        setFunctions: (partialFn = {}) => {
          const currentFns = get().fn || {};
          set({ fn: { ...currentFns, ...partialFn } });
        },

        setList: value => {
          set({
            currentPage: {
              ...get().currentPage,
              list: { ...get().currentPage?.list, ...value },
            },
          });
        },

        setParameters: value => set({ parameters: value }),

        setCurrentRouteKey: value => set({ currentRouteKey: value }),

        setFormDebug: formData => {
          set({ formDebug: formData || {} });
        },

        clearFormDebug: () => {
          set({ formDebug: {} });
        },

        setBreadcrumbs: (arg = {}) => {
          const current = Array.isArray(get().breadcrumbs) ? [...get().breadcrumbs] : [];
          const input = Array.isArray(arg)
            ? { add: arg, remove: null, reset: true }
            : { add: arg.add ?? null, remove: arg.remove ?? null, reset: !!arg.reset };

          const addArr = normalizeSegments(input.add);
          const removeArr = normalizeSegments(input.remove);

          let next = [...current];
          if (input.reset === true) {
            next = dedupePreserveOrder(addArr);
            set({ breadcrumbs: next });
            return;
          }
          if (removeArr.length) next = next.filter(seg => !removeArr.includes(seg));
          if (addArr.length) {
            const toAppend = addArr.filter(seg => !next.includes(seg));
            next = [...next, ...toAppend];
          }
          set({ breadcrumbs: next });
        },

        // setBackToPageActive: value => set({ backToPageActive: value }),

        // initFunctions: async afterInitCallback => {
        //   get().setFunctions({
        //     refreshParameters: async (url = API_MODULE_PARAMETERS) => {
        //       try {
        //         const [docs] = await axiosService.get({ url, returnAsObject: true });
        //         const parameters = clone(docs);
        //         get().setParameters(parameters);
        //         return parameters;
        //       } catch (err) {
        //         console.error('Module get parameters error:', err);
        //         throw err;
        //       }
        //     },
        //   });
        //   if (typeof afterInitCallback === 'function') afterInitCallback();
        // },
      };
    },
    {
      name: 'moduleStore',
      enabled: true,
    }
  )
);

// *******************************************************************************
// EXPORTS
// *******************************************************************************
export const useModuleStore = store;
export const moduleStore = store.getState;

// *******************************************************************************
// SELECTORS (For non-React contexts)
// *******************************************************************************
export const fromModuleStore = selector => {
  if (typeof selector === 'function') {
    return selector(useModuleStore.getState());
  }
  return useModuleStore.getState();
};

// *******************************************************************************
// HELPERS
// *******************************************************************************
function normalizeSegments(input) {
  if (Array.isArray(input)) {
    return input
      .filter(isRenderableSeg)
      .map(s => (typeof s === 'number' ? String(s) : s))
      .map(s => String(s).split('?')[0].trim())
      .filter(isValidSeg);
  }
  if (isRenderableSeg(input)) {
    return [String(input).split('?')[0].trim()].filter(isValidSeg);
  }
  return [];
}

function isRenderableSeg(v) {
  return (typeof v === 'string' || typeof v === 'number') && String(v).trim().length > 0;
}

function isValidSeg(v) {
  return typeof v === 'string' && v.trim().length > 0 && v.trim() !== 'undefined';
}

function dedupePreserveOrder(arr) {
  const seen = new Set();
  const out = [];
  arr.forEach(s => {
    if (!seen.has(s)) {
      seen.add(s);
      out.push(s);
    }
  });
  return out;
}
